import { BaseComponent } from './../../../../common/components/base.component';
import { DateRangePickerComponent } from 'src/app/common/components/date.range.picker/date.range.picker.component';
import { AccordionComponent } from 'src/app/common/components/accordion/accordion.component';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CalendarQueryCommand } from 'src/app/modules/shared/models/calendar.action.command';
import { VisitorEventService } from '../../services/visitor.event.service';
import { select, Store } from '@ngrx/store';
import { IAppState } from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs';
import { eventQueryCommandSelector } from '../../ngrx.stores/event.query.command/selectors';
import { Location } from '@angular/common';
import { EventTimeService, IDateRange } from '../../services/event.time.service';

@Component({
  selector: 'ig-event-date-filter',
  templateUrl: './event.date.filter.component.html',
  styleUrls: ['./event.date.filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EventDateFilterComponent extends BaseComponent {
  @Input() mode: 'expand' | 'dropdown' = 'expand';
  @Input() menuClass: string = '';
  @ViewChild('accordion', { static: true }) accordion: AccordionComponent;
  @ViewChild('dateRangePicker', { static: false })
  dateRangePicker: DateRangePickerComponent;

  isShowSpecificDates = false;
  title = 'FILTER BY DATE';

  originLink: string = '';

  private get timezoneFormatter() {
    return this.frontendSettings?.customSettings?.timezoneFormatter;
  }

  @Output() dateSelected = new EventEmitter<{
    from: Date;
    to: Date;
    dateRange: string;
  }>();

  constructor(
    public activatedRoute: ActivatedRoute,
    private _eventService: VisitorEventService,
    private _eventTimeService: EventTimeService,
    private _router: Router,
    private _location: Location,
    private _store: Store<IAppState>
  ) {
    super({ activatedRoute });

    this._store
      .pipe(takeUntil(this.isDestroyed), select(eventQueryCommandSelector))
      .subscribe((cmd: CalendarQueryCommand) => {
        if (!cmd) return;

        if (cmd.dateRange) {
          switch (cmd.dateRange) {
            case 'today':
              this.title = 'TODAY';
              break;
            case 'tomorrow':
              this.title = 'TOMORROW';
              break;
            case 'this-week':
              this.title = 'THIS WEEK';
              break;
            case 'this-weekend':
              this.title = 'THIS WEEKEND';
              break;
            case 'this-month':
              this.title = 'THIS MONTH';
              break;
            case 'specific':
              if (cmd.from && cmd.to) {
                const from = this._eventTimeService.toDateFilterTime(new Date(cmd.from), this.timezoneFormatter);
                const to = this._eventTimeService.toDateFilterTime(new Date(cmd.to), this.timezoneFormatter);
                this.title = `FROM ${from} TO ${to}`;
              } else {
                this.title = 'FILTER BY DATE';
              }
              break;
            default:
              this.title = 'FILTER BY DATE';
              break;
          }

          const currentPath = this._location.path();
          if (cmd.dateRange === 'specific' && cmd.from && cmd.to) {
            // Use ?start=YYYY-MM-DD&end=YYYY-MM-DD format for specific date ranges
            const urlWithoutParams = currentPath.split('?')[0];
            const startDate = this._eventTimeService.toDateFormat(cmd.from, this.timezoneFormatter);
            const endDate = this._eventTimeService.toDateFormat(cmd.to, this.timezoneFormatter);
            
            // Check if URL already has the correct dates to avoid unnecessary updates
            const urlParams = new URLSearchParams(window.location.search);
            const currentStart = urlParams.get('start');
            const currentEnd = urlParams.get('end');
            
            // Only update URL if dates don't match (prevents infinite loop and date shifts)
            if (currentStart !== startDate || currentEnd !== endDate) {
              urlParams.delete('date');
              urlParams.delete('start');
              urlParams.delete('end');
              urlParams.set('start', startDate);
              urlParams.set('end', endDate);
              
              const newUrl = urlParams.toString() ? `${urlWithoutParams}?${urlParams.toString()}` : urlWithoutParams;
              this._location.go(newUrl);
            }
          } else if (cmd.dateRange !== 'specific') {
            // For non-specific date ranges, use ?date= format
            const urlWithoutParams = currentPath.split('?')[0];
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.delete('start');
            urlParams.delete('end');
            urlParams.set('date', cmd.dateRange);
            
            const newUrl = urlParams.toString() ? `${urlWithoutParams}?${urlParams.toString()}` : urlWithoutParams;
            this._location.go(newUrl);
          }
        }
      });
  }

  dateRangeChanged(dateRange: IDateRange) {
    const from = this.formatPickerDate(dateRange.start, 'MM/DD/YYYY');
    const to = this.formatPickerDate(dateRange.end, 'MM/DD/YYYY');

    this.title = 'FROM ' + from + ' TO ' + to;
    this.accordion.closeMenu();

    // Use ?start=YYYY-MM-DD&end=YYYY-MM-DD format instead of ?date=specific
    const currentPath = this._location.path();
    const urlWithoutParams = currentPath.split('?')[0];
    const startDate = this.formatPickerDate(dateRange.start, 'YYYY-MM-DD');
    const endDate = this.formatPickerDate(dateRange.end, 'YYYY-MM-DD');
    
    // Remove existing date/start/end params and rebuild URL
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('date');
    urlParams.delete('start');
    urlParams.delete('end');
    urlParams.set('start', startDate);
    urlParams.set('end', endDate);
    
    const newUrl = urlParams.toString() ? `${urlWithoutParams}?${urlParams.toString()}` : urlWithoutParams;
    window.history.replaceState(null, '', newUrl);

    this.changeDate('specific', dateRange);
  }

  setTitle(title: string) {
    this.title = title;
  }

  resetTitle() {
    this.title = 'FILTER BY DATE';
  }

  changeDate(date: string, dateRange?: IDateRange) {
    const command = new CalendarQueryCommand();
    let range: { from: Date; to: Date };

    switch (date) {
      case 'all':
        break;
      case 'today':
        range = this._eventTimeService.getTodayRange(this.timezoneFormatter);
        break;
      case 'tomorrow':
        range = this._eventTimeService.getTomorrowRange(this.timezoneFormatter);
        break;
      case 'this-week':
        range = this._eventTimeService.getWeekRange(this.timezoneFormatter);
        break;
      case 'this-weekend':
        range = this._eventTimeService.getWeekendRangeInTimezone(this.timezoneFormatter);
        break;
      case 'this-month':
        range = this._eventTimeService.getMonthRange(this.timezoneFormatter);
        break;
      case 'specific':
        range = this._eventTimeService.getSpecificRange(dateRange, this.timezoneFormatter);
        break;
      default:
        throw Error('Invalid date range');
    }

    if (range) {
      command.from = range.from;
      command.to = range.to;
    }

    if (date !== 'specific') {
      this.title = date === 'all' ? 'FILTER BY DATE' : date.toUpperCase();
      this.dateRangePicker?.reset();
      this.isShowSpecificDates = false;
    }

    this.dateSelected.emit({
      from: command.from,
      to: command.to,
      dateRange: date,
    });
  }

  clear(isEmitEvent: boolean = true) {
    isEmitEvent ? this.changeDate('all') : (this.title = 'FILTER BY DATE');
    this.dateRangePicker?.reset();
  }

  private formatPickerDate(date: Date, format: string) {
    const localDate = new Date(date);
    const year = localDate.getFullYear();
    const month = `${localDate.getMonth() + 1}`.padStart(2, '0');
    const day = `${localDate.getDate()}`.padStart(2, '0');

    if (format === 'YYYY-MM-DD') {
      return `${year}-${month}-${day}`;
    }

    return `${month}/${day}/${year}`;
  }

  /**
   * Focuses on the next focusable element in the menu
   */
  focusNext(currentElement: HTMLElement): void {
    const menuItems = Array.from(
      document.querySelectorAll('[role="menuitem"]:not([disabled])')
    ) as HTMLElement[];

    const currentIndex = menuItems.indexOf(currentElement);
    const nextIndex = (currentIndex + 1) % menuItems.length;
    menuItems[nextIndex]?.focus();
  }

  /**
   * Focuses on the previous focusable element in the menu
   */
  focusPrevious(currentElement: HTMLElement): void {
    const menuItems = Array.from(
      document.querySelectorAll('[role="menuitem"]:not([disabled])')
    ) as HTMLElement[];

    const currentIndex = menuItems.indexOf(currentElement);
    const prevIndex = (currentIndex - 1 + menuItems.length) % menuItems.length;
    menuItems[prevIndex]?.focus();
  }
}
